import { view } from '@yola/ws-sdk';

const { setResponsiveClass, removeResponsiveClass, setElementAttribute } = view.operations;
const { getPatchedResponsiveClass, removeResponsiveClasses } = view.helpers;

const isClassOperation = (operationFn) =>
  operationFn === setResponsiveClass || operationFn === removeResponsiveClass;

const patchClass = (currentClass, [operationFn, params]) => {
  if (operationFn === setResponsiveClass) {
    const [, className, value, breakpoint, autofill] = params;

    return getPatchedResponsiveClass({ currentClass, className, value, breakpoint, autofill });
  }

  const [, className, breakpoint] = params;

  return removeResponsiveClasses({ currentClass, className, breakpoint });
};

// `bulkViewOperations` builds every action upfront, before any of them is applied, so
// several `setResponsiveClass`/`removeResponsiveClass` operations for the same element
// all read the same, not-yet-updated `class` attribute and clobber each other on write.
// Collapsing them into a single `setElementAttribute` here makes them compose correctly.
const mergeResponsiveClassOperations = (operations) => {
  const classOperationsByElementId = new Map();

  operations.forEach((operation) => {
    const [operationFn, params = []] = operation;

    if (!isClassOperation(operationFn)) return;

    const [elementId] = params;
    const group = classOperationsByElementId.get(elementId) || [];

    group.push(operation);
    classOperationsByElementId.set(elementId, group);
  });

  const mergedOperationsByElementId = new Map();

  classOperationsByElementId.forEach((group, elementId) => {
    if (group.length < 2) return;

    const currentClass = view.accessors.getElementAttribute(elementId, 'class') || '';
    const finalClass = group.reduce(patchClass, currentClass);

    mergedOperationsByElementId.set(elementId, [
      setElementAttribute,
      [elementId, 'class', finalClass],
    ]);
  });

  if (!mergedOperationsByElementId.size) {
    return operations;
  }

  const mergedElementIds = new Set();

  return operations.reduce((result, operation) => {
    const [operationFn, params = []] = operation;

    if (!isClassOperation(operationFn)) {
      result.push(operation);
      return result;
    }

    const [elementId] = params;
    const mergedOperation = mergedOperationsByElementId.get(elementId);

    if (!mergedOperation) {
      result.push(operation);
      return result;
    }

    if (!mergedElementIds.has(elementId)) {
      mergedElementIds.add(elementId);
      result.push(mergedOperation);
    }

    return result;
  }, []);
};

export default mergeResponsiveClassOperations;
